// User Management Service - Admin user CRUD API calls

import api from './api';

// Types
export interface UserListItem {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  salesperson_id: string | null;
  auth_provider: string;
  created_at: string;
  last_login_at: string | null;
}

export interface UserDetail {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  salesperson_id: string | null;
  assigned_accounts: string[];
  scope_overrides: { add?: string[]; remove?: string[] } | null;
  effective_scopes: string[];
  auth_provider: string;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
  /** Other addresses that sign this person in (kanban a772074c). */
  alternate_emails: string[];
}

/** One alternate sign-in address, live or removed (the row is kept as audit). */
export interface EmailAliasItem {
  id: string;
  email: string;
  label: string | null;
  status: 'active' | 'removed' | string;
  created_at: string;
  created_by_user_id: string | null;
  reason: string | null;
  removed_at: string | null;
  removed_by_user_id: string | null;
}

export interface AddEmailAliasRequest {
  email: string;
  label?: string;
  reason?: string;
}

export interface UsersListResponse {
  total: number;
  users: UserListItem[];
}

export interface CreateUserRequest {
  email: string;
  name: string;
  password: string;
  role?: string;
  salesperson_id?: string;
  assigned_accounts?: string[];
  scope_overrides?: { add?: string[]; remove?: string[] };
}

export interface UpdateUserRequest {
  name?: string;
  email?: string;
  role?: string;
  status?: string;
  salesperson_id?: string | null;
  assigned_accounts?: string[];
  scope_overrides?: { add?: string[]; remove?: string[] };
}

export interface ResetPasswordRequest {
  new_password: string;
}

export interface SendSetPasswordLinkRequest {
  /** Also make the current password unusable right away (a rotation). */
  lock_current_password?: boolean;
  /** Why, for the audit row. */
  reason?: string;
}

export interface SendSetPasswordLinkResponse {
  token_id: string;
  status: 'sent' | 'send_failed' | string;
  email: string;
  expires_at: string;
  sent_email_id: string | null;
  locked_current_password: boolean;
  detail: string | null;
  message: string;
}

export interface ScopeInfo {
  scope: string;
  description: string;
}

export interface RoleInfo {
  role: string;
  label: string;
  default_scopes: string[];
}

// Build query string helper
function buildQueryString(params: Record<string, string | boolean | undefined>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      parts.push(`${key}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export const userManagementService = {
  /** List all users with optional filters */
  list: async (params?: {
    include_inactive?: boolean;
    role?: string;
    search?: string;
  }): Promise<UsersListResponse> => {
    const qs = buildQueryString({
      include_inactive: params?.include_inactive ? 'true' : undefined,
      role: params?.role,
      search: params?.search,
    });
    const response = await api.get<UsersListResponse>(`/admin/users/list${qs}`);
    return response.data;
  },

  /** Get a single user by ID */
  get: async (userId: string): Promise<UserDetail> => {
    const response = await api.get<UserDetail>(`/admin/users/${userId}`);
    return response.data;
  },

  /** Create a new user */
  create: async (data: CreateUserRequest): Promise<UserDetail> => {
    const response = await api.post<UserDetail>('/admin/users', data);
    return response.data;
  },

  /** Update a user */
  update: async (userId: string, data: UpdateUserRequest): Promise<UserDetail> => {
    const response = await api.patch<UserDetail>(`/admin/users/${userId}`, data);
    return response.data;
  },

  /** Reset a user's password */
  resetPassword: async (userId: string, data: ResetPasswordRequest): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(`/admin/users/${userId}/reset-password`, data);
    return response.data;
  },

  /** Email the person a single-use set-password link (never a password). */
  sendSetPasswordLink: async (
    userId: string,
    data: SendSetPasswordLinkRequest = {},
  ): Promise<SendSetPasswordLinkResponse> => {
    const response = await api.post<SendSetPasswordLinkResponse>(
      `/admin/users/${userId}/send-set-password-link`,
      data,
    );
    return response.data;
  },

  /** Every address ever attached to the account, active first. */
  listEmailAliases: async (userId: string): Promise<EmailAliasItem[]> => {
    const response = await api.get<EmailAliasItem[]>(`/admin/users/${userId}/emails`);
    return response.data;
  },

  /** Let the person sign in with one more address (409 when it is someone else's). */
  addEmailAlias: async (userId: string, data: AddEmailAliasRequest): Promise<EmailAliasItem> => {
    const response = await api.post<EmailAliasItem>(`/admin/users/${userId}/emails`, data);
    return response.data;
  },

  /** Stop an address signing the person in; the row is kept as removed. */
  removeEmailAlias: async (userId: string, aliasId: string): Promise<EmailAliasItem> => {
    const response = await api.delete<EmailAliasItem>(`/admin/users/${userId}/emails/${aliasId}`);
    return response.data;
  },

  /** Deactivate a user (soft delete) */
  deactivate: async (userId: string): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/admin/users/${userId}`);
    return response.data;
  },

  /** Reactivate a deactivated user */
  reactivate: async (userId: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(`/admin/users/${userId}/reactivate`);
    return response.data;
  },

  /** List all available scopes */
  listScopes: async (): Promise<ScopeInfo[]> => {
    const response = await api.get<ScopeInfo[]>('/admin/users/scopes');
    return response.data;
  },

  /** List all available roles with default scopes */
  listRoles: async (): Promise<RoleInfo[]> => {
    const response = await api.get<RoleInfo[]>('/admin/users/roles');
    return response.data;
  },
};
